import React, { useState, useMemo, useEffect } from 'react';
import { MapContainer, TileLayer, GeoJSON, Marker, Polygon } from 'react-leaflet';
import { MAP_CONFIG, REGION_LABELS, MASK_POLYGONS } from './config/constants';
import { fetchMapData } from './data/staticData';
import { useTheme } from './context/ThemeContext';
import AQIMarker from './AQIMarker';
import AQILegend from './AQILegend';
import MarkerCluster from './MarkerCluster';
import DetailCard from './DetailCard';
import MapHeader, { MapControls } from './MapHeader';
import PerformanceZones from './PerformanceZones';
import PolesTable from './PolesTable';
import indiaBoundary from './data/india-boundary.json';
import L from 'leaflet';

const AQIMap = () => {
    const [selectedStation, setSelectedStation] = useState(null);
    const [isMapCollapsed, setIsMapCollapsed] = useState(false);
    const { isDark, theme } = useTheme();
    const [stations, setStations] = useState([]);
    const [mapFilters, setMapFilters] = useState({ site: '', location: '', status: '' });
    const [refreshKey, setRefreshKey] = useState(0);

    // Load data
    useEffect(() => {
        const loadData = async () => {
            const data = await fetchMapData();
            setStations(data);
            setRefreshKey(prev => prev + 1);
        };
        loadData();

        const intervalId = setInterval(loadData, 5 * 60 * 1000);
        return () => clearInterval(intervalId);
    }, []);

    // Handle marker click
    const handleMarkerClick = (stationUid) => {
        const details = stations.find(s => s.uid === stationUid) || null;
        setSelectedStation(details);
    };

    // Close detail card
    const handleCloseDetail = () => {
        setSelectedStation(null);
    };

    // Memoized markers (performance boost)
    const markers = useMemo(() => {
        // filter stations by MapHeader dropdowns
        const filteredStations = stations.filter(s => {
            // Check status
            if (mapFilters.status) {
                const sStatus = String(s.poleStatus || '').trim().toLowerCase();
                const sStatusText = String(s.status_text || '').trim().toLowerCase();
                const fStatus = String(mapFilters.status).trim().toLowerCase();
                if (sStatus !== fStatus && sStatusText !== fStatus) return false;
            }
            // Check site / allowedSites (country & state cascade)
            if (mapFilters.allowedSites) {
                const allowed = mapFilters.allowedSites.map(name => String(name).trim().toUpperCase());
                if (!allowed.includes(String(s.siteName).trim().toUpperCase())) return false;
            } else if (mapFilters.site) {
                if (String(s.siteName).trim().toUpperCase() !== String(mapFilters.site).trim().toUpperCase()) {
                    return false;
                }
            }
            // Check location
            if (mapFilters.location) {
                if (String(s.locationName).trim().toUpperCase() !== String(mapFilters.location).trim().toUpperCase()) {
                    return false;
                }
            }

            return true;
        });

        return filteredStations.map(station => (
            <AQIMarker
                key={station.uid}
                station={station}
                onClick={() => handleMarkerClick(station.uid)}
                isSelected={selectedStation?.uid === station.uid}
            />
        ));
    }, [stations, selectedStation, mapFilters]);

    // ✅ Optimized tile URL (OpenStreetMap)
    const tileUrl = useMemo(() => {
        return "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
    }, []);

    // Style for the official India boundary line
    const boundaryStyle = useMemo(() => ({
        color: '#ccc',
        weight: 2,
        opacity: 0.9,
        fillColor: 'transparent',
        fillOpacity: 0,
        interactive: false
    }), []);

    // Style for the masking polygons to cover disputed background labels
    const maskStyle = useMemo(() => ({
        fillColor: isDark ? '#393939ff' : '#e8e5dc',
        fillOpacity: 0.9,
        stroke: false,
        interactive: false
    }), [isDark]);

    // Render official region labels that mask underlying basemap labels
    const regionLabels = useMemo(() => {
        return REGION_LABELS.map(label => {
            const icon = L.divIcon({
                className: 'map-text-label-container',
                html: `<div class="map-text-label-mask">${label.name}</div>`,
                iconSize: [140, 30],
                iconAnchor: [70, 15]
            });
            return (
                <Marker
                    key={label.name}
                    position={label.position}
                    icon={icon}
                    interactive={false}
                />
            );
        });
    }, []);
    return (
        <div className={`aqi-map-container ${theme}`}>

            {/* Header */}
            <MapHeader onFilterChange={setMapFilters} stations={stations} />

            {/* Section Header */}
            <div className="map-section-header">
                <h2 className="section-title"></h2>
                <button
                    className="collapse-btn"
                    onClick={() => setIsMapCollapsed(!isMapCollapsed)}
                    aria-label={isMapCollapsed ? "Expand" : "Collapse"}
                >
                    {isMapCollapsed ? '▼' : '▲'}
                </button>
            </div>

            {/* Map Section */}
            {!isMapCollapsed && (
                <div className="aqi-map-wrapper">

                    <MapContainer
                        center={MAP_CONFIG.CENTER}
                        zoom={MAP_CONFIG.ZOOM}
                        minZoom={MAP_CONFIG.MIN_ZOOM}
                        maxZoom={MAP_CONFIG.MAX_ZOOM}
                        className="aqi-map"
                        zoomControl={true}
                        preferCanvas={true}   // ✅ improves performance with many markers
                    >
                        <TileLayer
                            url={tileUrl}
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                            maxZoom={19}
                        />
                        {/* Solid vector masks to hide disputed basemap borders & labels */}
                        {MASK_POLYGONS.map((positions, index) => (
                            <Polygon
                                key={`mask-${index}`}
                                positions={positions}
                                pathOptions={maskStyle}
                            />
                        ))}

                        {/* Official India boundary overlay */}
                        <GeoJSON
                            data={indiaBoundary}
                            style={boundaryStyle}
                        />

                        {/* Official Region Labels (Jammu & Kashmir, Ladakh) with masking backdrops */}
                        {regionLabels}

                        <MarkerCluster>
                            {markers}
                        </MarkerCluster>
                    </MapContainer>

                    {/* Controls inside map */}
                    <MapControls />

                    {/* Detail Card */}
                    {selectedStation && (
                        <DetailCard
                            station={selectedStation}
                            onClose={handleCloseDetail}
                            refreshTrigger={refreshKey}
                        />
                    )}

                    {/* Legend */}
                    {/* <AQILegend /> */}
                </div>
            )}

            {/* Extra Sections */}
            {/* <PerformanceZones /> */}
            <PolesTable refreshTrigger={refreshKey} />
        </div>
    );
};

export default AQIMap;